import { FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as vp from 'vopay';
import { withStyles, formattedCurrency, UtilButton, CollapsibleCard as VCollapisbleCard } from 'vopay';

import { CollapsibleCard, DetailsCard, DetailRow, Title, Button } from 'UI';
import { HubspotData } from '../Hubspot/Hubspot';
import { BillingData } from '../Billing/Billing';
import { RiskData } from '../Risk/Risk';
import {
  PLAN_MODEL_KEYS,
  railDisplayLabel,
  isPermissionAllowedForCurrency,
  sectionHasCurrencyAllowedPermission,
} from '../Billing/PackageDetails/utils/planSettingHelpers';

import styles from './Review.module.scss';

const cn = withStyles(styles);

const CARD_PADDING = { '--collapsibleCardPadding': '24px' } as React.CSSProperties;

export type ReviewProps = {
  prevStep: () => void;
  onSubmit: () => void;
  hubspot: HubspotData | null;
  billing: BillingData | null;
  risk: RiskData | null;
};

function capitalize(str: string): string {
  return str
    .split(' ')
    .map((s) => s && s[0].toUpperCase() + s.slice(1))
    .join(' ');
}

function formatSettingValue(type: string, value: string | boolean | null | undefined, currency: string): string {
  if (value === null || value === undefined || value === '') return '-';
  switch (type) {
    case 'boolean':
      return value === true || value === 'true' ? 'On' : 'Off';
    case 'amount':
      return `${currency} ${formattedCurrency(String(value))}`;
    case 'month': {
      const count = parseInt(String(value), 10);
      return isNaN(count) ? String(value) : `${count} ${count === 1 ? 'Month' : 'Months'}`;
    }
    default:
      return capitalize(String(value));
  }
}

function formatFeeValue(type: string, value: string | boolean | null | undefined, currency: string): string {
  const v = String(value ?? '0');
  switch (type) {
    case 'percent':
      return `% ${formattedCurrency(v)}`;
    case 'amount':
      return `${currency} ${formattedCurrency(v)}`;
    case 'boolean':
      return value === true || value === 'true' ? 'On' : 'Off';
    default:
      return v;
  }
}

type ReadOnlyServiceCardProps = {
  serviceName: string;
  planSetting: vp.PlanSetting;
  values: Record<string, string | boolean>;
  currency: string;
  planType: 'pay as you go' | 'subscription';
  useProgressivePricing: boolean;
};

const ReadOnlyServiceCard: FunctionComponent<ReadOnlyServiceCardProps> = ({
  serviceName,
  planSetting,
  values,
  currency,
  planType,
  useProgressivePricing,
}) => {
  const { permissions, maxAmounts, subscription, payAsYouGo, common, tiers } = planSetting;
  const permEntries = Object.entries(permissions).filter(([key]) => !PLAN_MODEL_KEYS.has(key));
  const permissionsOn = permEntries.length === 0 || permEntries.some(([key]) => Boolean(values[key]));

  const feeEntries = [
    ...Object.entries(common),
    ...(planType === 'pay as you go' ? Object.entries(payAsYouGo) : []),
    ...(planType === 'subscription' ? Object.entries(subscription) : []),
  ];
  const tierEntries = Object.entries(tiers);
  const hasNestedContent = permissionsOn && (feeEntries.length > 0 || tierEntries.length > 0);

  return (
    <DetailsCard>
      {permEntries.length === 0 && <DetailRow label={railDisplayLabel(serviceName, currency)} value={null} />}
      {permEntries.map(([key, fee]) => (
        <DetailRow
          key={key}
          label={railDisplayLabel(fee.label || serviceName, currency)}
          value={Boolean(values[key]) ? 'On' : 'Off'}
        />
      ))}
      {permissionsOn &&
        Object.entries(maxAmounts).map(([key, fee]) => (
          <DetailRow
            key={key}
            label={fee.label}
            value={`${currency} ${formattedCurrency(String(values[key] ?? fee.value ?? '0'))}`}
          />
        ))}
      {hasNestedContent && (
        <div className={cn('nestedCard')}>
          <DetailsCard className={cn('nestedDetailsCard')}>
            {feeEntries.map(([key, fee]) => (
              <DetailRow
                key={key}
                label={fee.label}
                value={formatFeeValue(fee.type, values[key] ?? fee.value, currency)}
              />
            ))}
            {useProgressivePricing &&
              tierEntries.map(([tierKey, tier]) => {
                const typedTier = tier as vp.TierFee<string>;
                const val = String(values[tierKey] ?? typedTier.value ?? '');
                const selected = Object.entries(typedTier.options ?? {}).find(([k]) => k === val);
                if (!selected) return null;
                return (
                  <DetailRow
                    key={tierKey}
                    label={typedTier.label}
                    value={(selected[1] as vp.TierOption<string>).label}
                  />
                );
              })}
          </DetailsCard>
        </div>
      )}
    </DetailsCard>
  );
};

const Review: FunctionComponent<ReviewProps> = ({ prevStep, onSubmit, hubspot, billing, risk }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'R1AccountCreation.Review' });
  const { t: tRails } = useTranslation('translations', {
    keyPrefix: 'R1AccountCreation.Billing.PackageDetails.PaymentRails',
  });

  const currency = useMemo(() => {
    const agreementDetails = billing?.billingPackageInfo?.AgreementDetails;
    if (agreementDetails) {
      const currencyEntry = Object.entries(agreementDetails).find(([key]) => key === 'Currency');
      const settingsKey = currencyEntry?.[1].settings;
      if (settingsKey) {
        const userCurrency = billing?.packageDetails?.agreementDetails?.[settingsKey];
        if (typeof userCurrency === 'string' && userCurrency) return userCurrency;
      }
    }
    return billing?.billingPackageInfo?.BillingSettings?.Currency.value ?? 'CAD';
  }, [billing]);
  const planType = billing?.billingPackageInfo?.BillingSettings?.PlanType.value ?? 'subscription';

  const collectRails = useMemo(() => {
    const rails = billing?.billingPackageInfo?.PaymentRails;
    const userValues = billing?.packageDetails?.paymentRails;
    if (!rails) return [];
    return Object.entries(rails)
      .filter(
        ([, ps]) =>
          ps.flowType === 'collect' &&
          Object.keys(ps.permissions).some(
            (k) => userValues?.[k] === true && isPermissionAllowedForCurrency(k, currency),
          ),
      )
      .map(([displayName, ps]) => ({
        key: Object.keys(ps.permissions).find((k) => isPermissionAllowedForCurrency(k, currency)) ?? displayName,
        label: railDisplayLabel(displayName, currency),
      }));
  }, [billing, currency]);

  const sendRails = useMemo(() => {
    const rails = billing?.billingPackageInfo?.PaymentRails;
    const userValues = billing?.packageDetails?.paymentRails;
    if (!rails) return [];
    return Object.entries(rails)
      .filter(
        ([, ps]) =>
          ps.flowType === 'send' &&
          Object.keys(ps.permissions).some(
            (k) => userValues?.[k] === true && isPermissionAllowedForCurrency(k, currency),
          ),
      )
      .map(([displayName, ps]) => ({
        key: Object.keys(ps.permissions).find((k) => isPermissionAllowedForCurrency(k, currency)) ?? displayName,
        label: railDisplayLabel(displayName, currency),
      }));
  }, [billing, currency]);

  const riskMatrix = risk?.riskMatrix ?? {};

  const handleDownloadFlowOfFunds = () => {
    if (!risk?.flowOfFundsFile || !risk?.flowOfFundsFileName) return;

    const commaIndex = risk.flowOfFundsFile.indexOf(',');
    const header = commaIndex >= 0 ? risk.flowOfFundsFile.slice(0, commaIndex) : '';
    const base64 = commaIndex >= 0 ? risk.flowOfFundsFile.slice(commaIndex + 1) : risk.flowOfFundsFile;
    const mime = header.match(/data:([^;]+)/)?.[1] || 'application/octet-stream';
    const byteCharacters = atob(base64);
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) byteArray[i] = byteCharacters.charCodeAt(i);
    const blob = new Blob([byteArray], { type: mime });
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
  };

  const renderRailsTable = (sectionTitle: string, rails: { key: string; label: string }[]) => {
    if (rails.length === 0) return null;

    return (
      <DetailsCard className={cn('railsTableCard')}>
        <div className={cn('railsContentContainer')}>
          <Title level="6" className={cn('sectionTitle')}>
            {sectionTitle}
          </Title>
          <div className={cn('tableContainer', 'nestedTableCard')}>
            <table>
              <thead>
                <tr>
                  <th>{t('Risk.paymentRail', { defaultValue: 'Payment Rail' })}</th>
                  <th>{t('Risk.transactionCount', { defaultValue: 'Transaction Count' })}</th>
                  <th>{t('Risk.transactionAmount', { defaultValue: 'Transaction Amount' })}</th>
                  <th>{t('Risk.returnChargeback', { defaultValue: 'Return/Chargeback %' })}</th>
                  <th>{t('Risk.frequency', { defaultValue: 'Frequency' })}</th>
                </tr>
              </thead>
              <tbody>
                {rails.map(({ key, label }) => (
                  <tr key={key}>
                    <td className={cn('railLabel')}>{label}</td>
                    <td>{riskMatrix[`${key}_count`] || '-'}</td>
                    <td>
                      {riskMatrix[`${key}_amount`]
                        ? `${currency} ${formattedCurrency(riskMatrix[`${key}_amount`])}`
                        : '-'}
                    </td>
                    <td>
                      {riskMatrix[`${key}_returnChargebackPercent`]
                        ? `% ${riskMatrix[`${key}_returnChargebackPercent`]}`
                        : '-'}
                    </td>
                    <td>
                      {riskMatrix[`${key}_frequency`]
                        ? t(`Risk.frequencyOptions.${riskMatrix[`${key}_frequency`]}`, {
                            defaultValue: capitalize(riskMatrix[`${key}_frequency`]),
                          })
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </DetailsCard>
    );
  };

  const billingPackageInfo = billing?.billingPackageInfo;
  const packageDetails = billing?.packageDetails ?? {};
  const agreementValues = packageDetails.agreementDetails ?? {};
  const configValues = packageDetails.accountConfiguration ?? {};
  const settingsValues = packageDetails.accountSettings ?? {};
  const railsValues = packageDetails.paymentRails ?? {};
  const servicesValues = packageDetails.additionalServices ?? {};

  const agreementEntries = Object.entries(billingPackageInfo?.AgreementDetails ?? {}).filter(
    ([, item]) => item.visible,
  );
  // Mirror the Account Configuration tab's controlling-boolean gating: a non-boolean/non-select
  // field (e.g. Sub-Accounts Fee) is only shown when its immediately-preceding boolean toggle
  // (e.g. Sub-Accounts Enabled) is on. Without this the fee shows even when sub-accounts are off.
  let configControllingBooleanOn = true;
  const configEntries = Object.entries(billingPackageInfo?.AccountConfiguration ?? {}).filter(([, item]) => {
    const gated = item.type !== 'boolean' && item.type !== 'select';
    const shown = item.visible && (!gated || configControllingBooleanOn);
    if (item.type === 'boolean') configControllingBooleanOn = Boolean(configValues[item.settings] ?? item.value);
    return shown;
  });
  const accountSettingsEntries = Object.entries(billingPackageInfo?.AccountSettings ?? {}).filter(
    ([key]) => !PLAN_MODEL_KEYS.has(key),
  );
  const paymentRailsEntries = Object.entries(billingPackageInfo?.PaymentRails ?? {}).filter(
    ([key, ps]) => !PLAN_MODEL_KEYS.has(key) && sectionHasCurrencyAllowedPermission(ps.permissions, currency),
  );
  const additionalServicesEntries = Object.entries(billingPackageInfo?.AdditionalServices ?? {}).filter(
    ([key]) => !PLAN_MODEL_KEYS.has(key),
  );

  return (
    <div className={cn('container')}>
      <div className={cn('header')}>
        <Title level="1">{t('title', { defaultValue: 'Account Details Review' })}</Title>
        <div className={cn('description')}>
          {t('description', { defaultValue: 'Please review the details that you have provided.' })}
        </div>
      </div>
      <VCollapisbleCard defaultOpen className={cn('reviewCard')}>
        <VCollapisbleCard.Trigger indicator="chevron" style={CARD_PADDING}>
          {t('Sections.details', { defaultValue: 'Details' })}
        </VCollapisbleCard.Trigger>
        <VCollapisbleCard.Content style={CARD_PADDING}>
          {hubspot?.details && (
            <div className={cn('sectionGroup')}>
              <div>
                <DetailRow
                  label={t('Details.hubSpotDealId', { defaultValue: 'HubSpot Deal ID' })}
                  value={hubspot.form.hubSpotDealId}
                />
                <DetailRow
                  label={t('Details.hubSpotCompanyId', { defaultValue: 'HubSpot Company ID' })}
                  value={hubspot.details.CompanyProfile.CompanyHubspotID ?? '-'}
                />
                <DetailRow
                  label={t('Details.country', { defaultValue: 'Country' })}
                  value={hubspot.details.CompanyProfile.Country ?? '-'}
                />
                <DetailRow
                  label={t('Details.accountLink', { defaultValue: 'HubSpot Account Link' })}
                  value={
                    hubspot.details.CompanyProfile.AccountLink ? (
                      <a href={hubspot.details.CompanyProfile.AccountLink} target="_blank" rel="noreferrer">
                        {hubspot.details.CompanyProfile.AccountLink}
                      </a>
                    ) : (
                      '-'
                    )
                  }
                />
              </div>
              <div>
                <Title level="6" className={cn('sectionLabel')}>
                  {t('Details.primaryContact', { defaultValue: 'Primary Contact Information' })}
                </Title>
                <DetailRow
                  label={t('Details.firstName', { defaultValue: 'First Name' })}
                  value={hubspot.details.PrimaryContact.firstname ?? '-'}
                />
                <DetailRow
                  label={t('Details.lastName', { defaultValue: 'Last Name' })}
                  value={hubspot.details.PrimaryContact.lastname ?? '-'}
                />
                <DetailRow
                  label={t('Details.email', { defaultValue: 'Email Address' })}
                  value={hubspot.details.PrimaryContact.email ?? '-'}
                />
              </div>
              <div>
                <Title level="6" className={cn('sectionLabel')}>
                  {t('Details.businessInfo', { defaultValue: 'Business Information' })}
                </Title>
                <DetailRow
                  label={t('Details.legalName', { defaultValue: 'Legal Business Name' })}
                  value={hubspot.details.BusinessInformation.CompanyName ?? '-'}
                />
                <DetailRow
                  label={t('Details.dba', { defaultValue: 'Doing-Business-As (DBA) Name' })}
                  value={hubspot.details.BusinessInformation.Dba ?? '-'}
                />
                <DetailRow
                  label={t('Details.website', { defaultValue: 'Company Website' })}
                  value={
                    hubspot.details.BusinessInformation.CompanyWebsite ? (
                      <a href={hubspot.details.BusinessInformation.CompanyWebsite} target="_blank" rel="noreferrer">
                        {hubspot.details.BusinessInformation.CompanyWebsite}
                      </a>
                    ) : (
                      '-'
                    )
                  }
                />
                <DetailRow
                  label={t('Details.socialMedia', { defaultValue: 'Business Social Media' })}
                  value={
                    hubspot.details.BusinessInformation.SocialMediaPage ? (
                      <a href={hubspot.details.BusinessInformation.SocialMediaPage} target="_blank" rel="noreferrer">
                        {hubspot.details.BusinessInformation.SocialMediaPage}
                      </a>
                    ) : (
                      '-'
                    )
                  }
                />
                <DetailRow
                  label={t('Details.phone', { defaultValue: 'Business Phone Number' })}
                  value={hubspot.details.BusinessInformation.CompanyPhone ?? '-'}
                />
                {hubspot.form.additionalNotes && (
                  <DetailRow
                    label={t('Details.additionalNotes', { defaultValue: 'Additional Notes' })}
                    value={hubspot.form.additionalNotes}
                  />
                )}
              </div>
              <div>
                <Title level="6" className={cn('sectionLabel')}>
                  {t('Details.paymentInfo', { defaultValue: 'Payment Information' })}
                </Title>
                <DetailRow
                  label={t('Details.paymentMethod', { defaultValue: 'Payment Method' })}
                  value={hubspot.details.BusinessInformation.PaymentMethod ?? '-'}
                />
              </div>
            </div>
          )}
        </VCollapisbleCard.Content>
      </VCollapisbleCard>

      <VCollapisbleCard defaultOpen className={cn('reviewCard')}>
        <VCollapisbleCard.Trigger indicator="chevron" style={CARD_PADDING}>
          {t('Sections.billing', { defaultValue: 'Billing' })}
        </VCollapisbleCard.Trigger>
        <VCollapisbleCard.Content style={CARD_PADDING}>
          <div className={cn('sectionGroup')}>
            <div>
              <div className={cn('packageContainer')}>
                {billingPackageInfo?.PackageSettings?.PackageName && (
                  <DetailRow
                    label={t('Billing.packageName', { defaultValue: 'Package' })}
                    value={billingPackageInfo.PackageSettings.PackageName}
                  />
                )}
              </div>

              <Title level="6" className={cn('sectionLabel')}>
                {t('Billing.agreementDetails', { defaultValue: 'Agreement Details' })}
              </Title>
              {agreementEntries.length > 0 && (
                <>
                  {agreementEntries.map(([key, item]) => (
                    <DetailRow
                      key={key}
                      label={key}
                      value={formatSettingValue(item.type, agreementValues[item.settings] ?? item.value, currency)}
                    />
                  ))}
                </>
              )}
            </div>
            {configEntries.length > 0 && (
              <CollapsibleCard defaultOpen>
                <CollapsibleCard.Trigger indicator="chevron" style={CARD_PADDING}>
                  {t('Billing.accountConfiguration', { defaultValue: 'Account Configuration' })}
                </CollapsibleCard.Trigger>
                <CollapsibleCard.Content style={CARD_PADDING}>
                  <div className={cn('serviceGroup')}>
                    {configEntries.map(([key, item]) => (
                      <DetailsCard key={key}>
                        <DetailRow
                          label={key}
                          value={formatSettingValue(item.type, configValues[item.settings] ?? item.value, currency)}
                        />
                      </DetailsCard>
                    ))}
                  </div>
                </CollapsibleCard.Content>
              </CollapsibleCard>
            )}

            {accountSettingsEntries.length > 0 && (
              <CollapsibleCard defaultOpen>
                <CollapsibleCard.Trigger indicator="chevron" style={CARD_PADDING}>
                  {t('Billing.accountSettings', { defaultValue: 'Account Settings' })}
                </CollapsibleCard.Trigger>
                <CollapsibleCard.Content style={CARD_PADDING}>
                  <div className={cn('serviceGroup')}>
                    {accountSettingsEntries.map(([name, planSetting]) => (
                      <ReadOnlyServiceCard
                        key={name}
                        serviceName={name}
                        planSetting={planSetting}
                        values={settingsValues}
                        currency={currency}
                        planType={planType}
                        useProgressivePricing={
                          billingPackageInfo?.BillingSettings?.UseProgressivePricing?.value ?? false
                        }
                      />
                    ))}
                  </div>
                </CollapsibleCard.Content>
              </CollapsibleCard>
            )}

            {paymentRailsEntries.length > 0 && (
              <CollapsibleCard defaultOpen>
                <CollapsibleCard.Trigger indicator="chevron" style={CARD_PADDING}>
                  {t('Billing.paymentRails', { defaultValue: 'Payment Rails' })}
                </CollapsibleCard.Trigger>
                <CollapsibleCard.Content style={CARD_PADDING}>
                  <div className={cn('groupContainer')}>
                    {(['send', 'collect', 'other'] as const).map((group) => {
                      const items = paymentRailsEntries.filter(([, ps]) =>
                        group === 'other' ? ps.flowType !== 'send' && ps.flowType !== 'collect' : ps.flowType === group,
                      );
                      if (items.length === 0) return null;
                      return (
                        <div key={group} className={cn('groupContainer')}>
                          <Title level="5" className={cn('groupLabel')}>
                            {tRails(group === 'send' ? 'sendRails' : group === 'collect' ? 'collectRails' : 'other', {
                              defaultValue:
                                group === 'send' ? 'Send Rails' : group === 'collect' ? 'Collect Rails' : 'Other',
                            })}
                          </Title>
                          <div className={cn('serviceGroup')}>
                            {items.map(([name, planSetting]) => (
                              <ReadOnlyServiceCard
                                key={name}
                                serviceName={name}
                                planSetting={planSetting}
                                values={railsValues}
                                currency={currency}
                                planType={planType}
                                useProgressivePricing={
                                  billingPackageInfo?.BillingSettings?.UseProgressivePricing?.value ?? false
                                }
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CollapsibleCard.Content>
              </CollapsibleCard>
            )}

            {additionalServicesEntries.length > 0 && (
              <CollapsibleCard defaultOpen>
                <CollapsibleCard.Trigger indicator="chevron" style={CARD_PADDING}>
                  {t('Billing.additionalServices', { defaultValue: 'Additional Services' })}
                </CollapsibleCard.Trigger>
                <CollapsibleCard.Content style={CARD_PADDING}>
                  <div className={cn('serviceGroup')}>
                    {additionalServicesEntries.map(([name, planSetting]) => (
                      <ReadOnlyServiceCard
                        key={name}
                        serviceName={name}
                        planSetting={planSetting}
                        values={servicesValues}
                        currency={currency}
                        planType={planType}
                        useProgressivePricing={
                          billingPackageInfo?.BillingSettings?.UseProgressivePricing?.value ?? false
                        }
                      />
                    ))}
                  </div>
                </CollapsibleCard.Content>
              </CollapsibleCard>
            )}
          </div>
        </VCollapisbleCard.Content>
      </VCollapisbleCard>

      <VCollapisbleCard defaultOpen className={cn('reviewCard')}>
        <VCollapisbleCard.Trigger indicator="chevron" style={CARD_PADDING}>
          {t('Sections.risk', { defaultValue: 'Risk' })}
        </VCollapisbleCard.Trigger>
        <VCollapisbleCard.Content style={CARD_PADDING}>
          <div className={cn('sectionGroup')}>
            {risk?.flowOfFundsFileName && (
              <div>
                <DetailRow
                  label={t('Risk.flowOfFunds', { defaultValue: 'Flow of Funds Diagram' })}
                  value={
                    <UtilButton
                      size="medium"
                      iconRightProps={{ icon: 'visibility', fillAmount: 1 }}
                      onClick={handleDownloadFlowOfFunds}
                    >
                      {risk.flowOfFundsFileName}
                    </UtilButton>
                  }
                />
                {(risk?.businessModelCollectingFromThirdParties ||
                  risk?.businessModelFundingFromOwnAccount ||
                  risk?.businessModelDistributingToThirdParties ||
                  risk?.businessModelActingAsIntermediary) && (
                  <DetailRow
                    label={t('Risk.businessModelType', { defaultValue: 'Business Model Type' })}
                    value={
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {risk.businessModelCollectingFromThirdParties && (
                          <span>
                            {t('BusinessModel.collectingFromThirdParties', {
                              defaultValue: 'Collecting from third parties',
                            })}
                          </span>
                        )}
                        {risk.businessModelFundingFromOwnAccount && (
                          <span>
                            {t('BusinessModel.fundingFromOwnAccount', {
                              defaultValue: 'Funding transactions from its own account',
                            })}
                          </span>
                        )}
                        {risk.businessModelDistributingToThirdParties && (
                          <span>
                            {t('BusinessModel.distributingToThirdParties', {
                              defaultValue: 'Distributing funds to third parties',
                            })}
                          </span>
                        )}
                        {risk.businessModelActingAsIntermediary && (
                          <span>
                            {t('BusinessModel.actingAsIntermediary', {
                              defaultValue: 'Acting purely as an intermediary between multiple parties',
                            })}
                          </span>
                        )}
                      </div>
                    }
                  />
                )}
                {risk.businessModelAdditionalContext && (
                  <DetailRow
                    label={t('Risk.additionalContext', { defaultValue: 'Additional Context' })}
                    value={risk.businessModelAdditionalContext}
                  />
                )}
              </div>
            )}
            {(collectRails.length > 0 || sendRails.length > 0) && (
              <>
                <Title level="5">{t('Risk.riskMatrix', { defaultValue: 'Risk Matrix' })}</Title>
                {renderRailsTable(
                  t('Risk.collectSection', { defaultValue: 'Funds collected from third parties' }),
                  collectRails,
                )}
                {renderRailsTable(t('Risk.sendSection', { defaultValue: 'Funds sent to third parties' }), sendRails)}
              </>
            )}
          </div>
        </VCollapisbleCard.Content>
      </VCollapisbleCard>

      <div className={cn('footer')}>
        <Button colorScheme="secondary" type="button" onClick={prevStep}>
          {t('editDetails', { defaultValue: 'Edit Details' })}
        </Button>
        <Button colorScheme="primary" type="button" onClick={onSubmit}>
          {t('addAccount', { defaultValue: 'Add Account' })}
        </Button>
      </div>
    </div>
  );
};

export default Review;
